import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  Skeleton,
  Avatar
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { RoleChip } from "./RoleSelector";
import {
  CameraExportButton,
  exportComponentAsImage
} from "../../../../utils/export/componentExporter";

const stringToColor = (string) => {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = Math.abs(hash % 2147483647).toString(16);
  return `#${color}`;
};

const PermissionTableView = ({
  companies = [],
  companyPermissions = {},
  allUsers = [],
  currentUserId,
  loadingCompanyPermissions = false
}) => {
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, `Permission Table`);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");

  // 数据处理逻辑 (保持原有逻辑，增加过滤)
  const { columnUsers, rows } = useMemo(() => {
    // 1. 构建用户映射
    const userById = {};
    (allUsers || []).forEach((u) => {
      if (u?.u_id && u.u_id !== currentUserId) userById[u.u_id] = u;
    });
    Object.values(companyPermissions || {}).forEach((bucket) => {
      const list = bucket?.userList || Object.values(bucket?.users || {});
      list.forEach((item) => {
        const uId = item?.u_id;
        if (!uId || uId === currentUserId) return;
        if (!userById[uId]) {
          userById[uId] = {
            u_id: uId,
            name: item?.user_name,
            email: item?.user_email
          };
        }
      });
    });

    // 2. 准备列数据 (Users)
    const allColumnUsers = Object.values(userById).filter(Boolean);

    // 3. 准备行数据 (Companies)
    const allRows = (companies || [])
      .filter((c) => c?.c_id)
      .map((company) => {
        const cId = company.c_id;
        const bucket = companyPermissions[cId] || { users: {}, company };
        const usersMap = bucket.users || {};
        return {
          company,
          cId,
          cells: allColumnUsers.map((user) => {
            const perm = usersMap[user.u_id] || null;
            const role = perm?.role ?? "none";
            const isPartial = perm?.is_partial_access ?? false;
            const yoa = perm?.allowed_yoa ?? [];
            return {
              u_id: user.u_id,
              role,
              isPartial,
              yoa: Array.isArray(yoa) ? yoa : []
            };
          })
        };
      });

    // 4. 应用搜索过滤
    const lowerTerm = searchTerm.toLowerCase();
    const filteredRows = allRows.filter((row) => {
      const name = (
        row.company?.name ||
        row.company?.company_name ||
        ""
      ).toLowerCase();
      const uen = (
        row.company?.uen ||
        row.company?.company_uen ||
        ""
      ).toLowerCase();
      return name.includes(lowerTerm) || uen.includes(lowerTerm);
    });

    return { columnUsers: allColumnUsers, rows: filteredRows };
  }, [companies, companyPermissions, allUsers, currentUserId, searchTerm]);

  if (loadingCompanyPermissions) {
    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200, bgcolor: "grey.50" }}>
                <Skeleton width='60%' />
              </TableCell>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableCell
                  key={i}
                  align='center'
                  sx={{ minWidth: 120, bgcolor: "grey.50" }}
                >
                  <Skeleton
                    variant='circular'
                    width={24}
                    height={24}
                    sx={{ mx: "auto", mb: 1 }}
                  />
                  <Skeleton width='80%' sx={{ mx: "auto" }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton width='90%' />
                </TableCell>
                {[1, 2, 3, 4, 5].map((j) => (
                  <TableCell key={j} align='center'>
                    <Skeleton width={40} height={24} sx={{ mx: "auto" }} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (rows.length === 0 || columnUsers.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: "center",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "grey.50"
        }}
      >
        <Box sx={{ opacity: 0.5, mb: 2 }}>
          <SearchIcon sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant='h6' color='text.secondary' gutterBottom>
          No data
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {searchTerm
            ? "Try adjusting the search keywords"
            : "No available company or user permission records"}
        </Typography>
        {searchTerm && (
          <Typography
            variant='body2'
            color='primary'
            sx={{ mt: 1, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setSearchTerm("")}
          >
            Clear search
          </Typography>
        )}
      </Paper>
    );
  }

  const companyName = (c) => c?.name || c?.company_name || "—";
  const userDisplay = (u) => u?.name || u?.email || `User ${u?.u_id}`;
  const userInitials = (u) => {
    const name = u?.name || u?.email || "U";
    return name.charAt(0).toUpperCase();
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      {/* 顶部工具栏：搜索 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size='small'
            placeholder='Search company name or UEN...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' color='action' />
                </InputAdornment>
              ),
              sx: { width: 300, bgcolor: "background.paper" }
            }}
            variant='outlined'
          />
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the Permission Table'
            sx={{ mr: 4 }}
          />
        </Box>
        <Typography variant='caption' color='text.secondary'>
          Total {rows.length} companies, {columnUsers.length} users
        </Typography>
      </Box>

      {/* 表格容器 */}
      <TableContainer
        ref={exportRef}
        component={Paper}
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "auto",
          maxHeight: "100%",
          // 关键：确保阴影能显示在固定列之上
          "& .MuiTableCell-root": { backgroundColor: "background.paper" }
        }}
      >
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {/* 固定公司列头 */}
              <TableCell
                sx={{
                  minWidth: 240,
                  fontWeight: 700,
                  bgcolor: "grey.50",
                  borderColor: "divider",
                  position: "sticky",
                  left: 0,
                  zIndex: 2, // 高于普通单元格
                  boxShadow: 2 // 右侧阴影
                }}
              >
                Company Information
              </TableCell>
              {columnUsers.map((user) => (
                <TableCell
                  key={user.u_id}
                  align='center'
                  sx={{
                    minWidth: 140,
                    fontWeight: 600,
                    bgcolor: "grey.50",
                    borderColor: "divider"
                  }}
                >
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant='subtitle2' fontWeight={700}>
                          {userDisplay(user)}
                        </Typography>
                        {user?.email && (
                          <Typography
                            variant='caption'
                            sx={{ wordBreak: "break-all" }}
                          >
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    }
                    placement='top'
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 12,
                          bgcolor: stringToColor(user.u_id),
                          cursor: "pointer"
                        }}
                      >
                        {userInitials(user)}
                      </Avatar>
                      <Typography
                        variant='caption'
                        noWrap
                        sx={{ maxWidth: 120, fontSize: 11 }}
                      >
                        {userDisplay(user)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.cId}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    bgcolor: "action.hover"
                  }
                }}
              >
                {/* 固定公司列内容 */}
                <TableCell
                  className='sticky-cell'
                  sx={{
                    minWidth: 240,
                    borderColor: "divider",
                    verticalAlign: "middle",
                    position: "sticky",
                    left: 0,
                    zIndex: 1, // 低于表头，高于普通内容
                    bgcolor: "background.paper" // 防止透明
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant='body2'
                      fontWeight={600}
                      noWrap
                      sx={{ maxWidth: 180 }}
                    >
                      {companyName(row.company)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontFamily: "monospace" }}
                    >
                      {row.company?.uen || row.company?.company_uen || "No UEN"}
                    </Typography>
                  </Box>
                </TableCell>

                {/* 权限单元格 */}
                {row.cells.map((cell) => {
                  const hasAccess = cell.role !== "none";
                  return (
                    <TableCell
                      key={cell.u_id}
                      align='center'
                      sx={{
                        borderColor: "divider",
                        verticalAlign: "middle",
                        py: 1
                      }}
                    >
                      <Tooltip
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant='caption' display='block'>
                              Role:{" "}
                              {cell.role.charAt(0).toUpperCase() +
                                cell.role.slice(1)}
                            </Typography>
                            {cell.isPartial ? (
                              <Typography
                                variant='caption'
                                display='block'
                                color='warning.light'
                              >
                                Partial access
                              </Typography>
                            ) : (
                              <Typography
                                variant='caption'
                                display='block'
                                color='success.light'
                              >
                                Full access
                              </Typography>
                            )}
                            {cell.yoa?.length > 0 && (
                              <Typography variant='caption' display='block'>
                                YOA: {cell.yoa.join(", ")}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement='right'
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                            ...(hasAccess && {
                              bgcolor:
                                cell.role === "admin"
                                  ? "error.50"
                                  : "primary.50",
                              borderRadius: 1,
                              p: 0.5
                            })
                          }}
                        >
                          {/* 使用原有的 RoleChip，但确保它在小尺寸下表现良好 */}
                          <RoleChip
                            role={cell.role}
                            size='small'
                            variant={hasAccess ? "filled" : "outlined"}
                          />

                          {/* 优化 YOA 显示：仅在有且较多时显示图标 */}
                          {cell.yoa?.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.3
                              }}
                            >
                              <Box
                                component='span'
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: cell.isPartial
                                    ? "warning.main"
                                    : "success.main"
                                }}
                              />
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                fontSize={10}
                              >
                                {cell.yoa.length} YOA
                              </Typography>
                            </Box>
                          )}
                          {/* 如果是部分权限，显示警告图标 */}
                          {cell.role !== "none" &&
                            cell.isPartial &&
                            cell.yoa?.length === 0 && (
                              <Typography
                                variant='caption'
                                color='warning.main'
                                fontSize={10}
                              >
                                Partial access
                              </Typography>
                            )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PermissionTableView;
